import { FieldLabel } from "../workspaces/shared/FieldLabel";
import { useEffect, useState } from "react";
import { userMessage } from "../workspaces/shared/userMessage";
import { Callout } from "../workspaces/shared/ui";
import { AuthLayout } from "./AuthLayout";
import { login, SERVER_UNREACHABLE, type HostedUser } from "./session";

/**
 * SIGN IN (Phase 13A; spec §86, redesigned §88A).
 *
 * "Forgot password?" answers the same way for an address that has an account
 * and one that does not. Sign-up lives on the public site when it is open.
 */
export function SignInScreen({
  onSignedIn,
  serverUnreachable,
  onForgotPassword,
  signupEnabled,
}: {
  onSignedIn: (user: HostedUser) => void;
  /** The page loaded, but its server is not answering. */
  serverUnreachable?: boolean;
  onForgotPassword?: () => void;
  /** Spec §86: link to self-service signup when this deployment has it open. */
  signupEnabled?: boolean;
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(serverUnreachable ? SERVER_UNREACHABLE : null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Sign in — Berrier Reno";
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      onSignedIn(await login(email, password));
      setError(null);
    } catch (err) {
      setError(userMessage(err, "That sign-in could not be completed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Welcome back"
      lede="Sign in to pick up your projects and deals where you left them."
      footer={
        signupEnabled ? (
          <>
            New to Berrier Reno? <a href="/signup">Start Early Access</a>
          </>
        ) : (
          "Accounts are by invitation during Early Access."
        )
      }
    >
      <form onSubmit={(event) => void submit(event)} className="auth-form">
        <FieldLabel>
          Email
          <input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus />
        </FieldLabel>
        <FieldLabel>
          <span className="auth-label-row">
            Password
            {onForgotPassword && (
              <button className="link-button auth-hint-link" type="button" onClick={onForgotPassword}>
                Forgot password?
              </button>
            )}
          </span>
          <input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required />
        </FieldLabel>
        {error && <Callout tone="bad">{error}</Callout>}
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        {serverUnreachable && (
          <button className="btn" type="button" onClick={() => window.location.reload()}>
            Try again
          </button>
        )}
      </form>
    </AuthLayout>
  );
}
